import { gql, useMutation } from '@apollo/client'
import { InAppBrowser } from '@capacitor/inappbrowser'
import { useEffect, useRef } from 'react'

import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { isNativePlatform } from '@/core/native/app'
import { LinkifyBox } from '@/lib/reactLinkify'

import { useSafeHtml } from './useSafeHtml'

export const SafeHtml = ({
  text,
  enable,
}: {
  text: string
  enable: boolean
}) => {
  /**
   * formのsubmitを外部ブラウザで開く際に許可するドメイン
   * ポスト内に入れるformタグのactionがこのドメインに含まれている場合に限り、formタグのsubmitをフックする。
   * - チケットぴあ
   */
  const nativeHookAllowedDomains = ['ticket.pia.jp']
  const containerRef = useRef<HTMLDivElement>(null)
  const { generateQueryPath } = useRouter()
  const { t } = useTranslation()

  const result = useSafeHtml({ text })

  /**
   * ネイティブアプリから外部ブラウザ経由でUDomにアクセスする際に、カスタムヘッダが付与できないため、
   * ワンタイムトークンを発行して付与する
   */
  const [generatePartnerClientToken] = useMutation(
    gql`
      mutation GeneratePartnerClientToken(
        $input: GeneratePartnerClientTokenInput!
      ) {
        generatePartnerClientToken(input: $input) {
          token
          expiredAt
        }
      }
    `
  )

  /**
   * ネイティブアプリの場合はポストにformタグを埋め込まれると、外部ドメインへのPOST処理が行えない。
   * そのため、ネイティブアプリの場合はformタグのsubmitをフックして、formのパラメータとともに外部ブラウザへGET処理を行う。
   */
  useEffect(() => {
    if (result !== 'safe') return
    if (!isNativePlatform()) return

    const container = containerRef.current
    if (!container) return

    container.innerHTML = text

    const forms = container.getElementsByTagName('form')
    if (!forms.length) return
    const cleanupFuncs: (() => void)[] = []

    for (const form of forms) {
      try {
        const action = form.getAttribute('action')
        if (!action) continue

        const url = new URL(action)
        if (!nativeHookAllowedDomains.includes(url.hostname)) continue

        const submitHandler = async (e: Event) => {
          e.preventDefault()

          // formの値をクエリ化してGETリクエストで渡せるようにする
          const formData = new FormData(form)
          const inputs = Array.from(formData.entries()).map(
            ([name, value]) => ({
              name,
              value: String(value),
            })
          )

          // ワンタイムトークンの発行
          let generatedToken: string | undefined
          if (config.partnerClientId && config.partnerClientToken) {
            const { data } = await generatePartnerClientToken({
              variables: {
                input: {
                  clientId: config.partnerClientId,
                  clientToken: config.partnerClientToken,
                },
              },
            })
            generatedToken = data?.generatePartnerClientToken?.token
          }

          const query = {
            action,
            method: form.method || 'post',
            inputs: JSON.stringify(inputs),
            ...(generatedToken
              ? { clientId: config.partnerClientId, token: generatedToken }
              : {}),
          }

          const to = generateQueryPath('/htmlFormRelay', undefined, query)
          InAppBrowser.openInExternalBrowser({
            url: `${config.domain}${to}`,
          }).catch((e: any) => {
            // リトライするしか方法がないので、エラーメッセージは共通のものを使用
            throw new Error(t('otherErrorMessage'))
          })
        }
        form.addEventListener('submit', submitHandler)
        cleanupFuncs.push(() =>
          form.removeEventListener('submit', submitHandler)
        )
      } catch {
        // 予期せぬエラーが発生してもコンテンツの閲覧はできるよう処理は継続する
        continue
      }
    }

    return () => {
      cleanupFuncs.forEach(f => f())
    }
  }, [text])

  return (
    <>
      {result === 'safe' &&
        (enable ? (
          <span ref={containerRef} dangerouslySetInnerHTML={{ __html: text }} />
        ) : null)}
      {result === 'unsafe' && <LinkifyBox>{text}</LinkifyBox>}
    </>
  )
}
